<template>
  <div class="ownership-transfer">
    <div v-if="loading" class="muted" style="margin-bottom: 12px">{{ __('Loading…') }}</div>
    <div v-else-if="proposals.length === 0" class="muted" style="margin-bottom: 12px">
      {{ s__('ProjectSettings|No ownership transfer proposals yet.') }}
    </div>
    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
      <li
        v-for="proposal in proposals"
        :key="proposal.id"
        class="proposal-item"
        style="padding: 12px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 8px"
      >
        <div style="display: flex; justify-content: space-between; align-items: flex-start">
          <div style="flex: 1">
            <div>
              <strong>{{
                proposal.proposedOwner.name ||
                proposal.proposedOwner.username ||
                `User ${proposal.proposedOwner.id}`
              }}</strong>
            </div>
            <div class="muted" style="font-size: 13px; margin-top: 2px">
              @{{ proposal.proposedOwner.username || 'no-username' }}
            </div>
            <div
              style="margin-top: 8px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap"
            >
              <span
                class="status-badge"
                :class="`status-${proposal.status}`"
                style="padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 500"
              >
                {{ formatProposalStatus(proposal.status) }}
              </span>
              <span v-if="proposal.deliveryAttempts > 0" class="muted" style="font-size: 12px">
                {{ s__('ProjectSettings|Attempts') }}: {{ proposal.deliveryAttempts }}/5
              </span>
            </div>
            <div
              v-if="proposal.errors && proposal.errors.length > 0"
              style="
                margin-top: 8px;
                padding: 6px 10px;
                background: #ffebee;
                border: 1px solid #ef5350;
                border-radius: 4px;
                font-size: 12px;
                color: #c62828;
              "
            >
              <div v-for="(error, idx) in proposal.errors" :key="idx" style="margin-top: 4px">
                {{ error.message || error }}
              </div>
            </div>
          </div>
          <div
            v-if="proposal.status === 'active' || proposal.status === 'to_deliver'"
            style="margin-left: 12px"
          >
            <button
              class="btn-sm btn-danger"
              :disabled="revokingProposalId === proposal.id"
              @click="revokeProposal(proposal)"
            >
              <span class="btn-text">{{ s__('ProjectSettings|Revoke') }}</span>
              <TrashIcon />
            </button>
          </div>
        </div>
      </li>
    </ul>
    <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #ddd">
      <h4 style="margin: 0 0 8px 0; font-size: 14px">
        {{ s__('ProjectSettings|Transfer Ownership') }}
      </h4>
      <div
        style="
          margin-bottom: 12px;
          padding: 8px;
          background-color: #f5f5f5;
          border-radius: 4px;
          font-size: 13px;
          color: #666;
        "
      >
        {{
          s__(
            'ProjectSettings|Select a project member to transfer ownership to. Only members of this project can be selected.'
          )
        }}
      </div>
      <div style="display: flex; gap: 8px; align-items: center">
        <select v-model="newProposalProposedOwnerId" class="form-select" style="flex: 1">
          <option :value="null">
            {{ s__('ProjectSettings|Select member to transfer ownership to') }}
          </option>
          <option
            v-for="membership in availableMembers"
            :key="membership.id"
            :value="membership.user.id"
          >
            {{ membership.user.name || membership.user.username || `User ${membership.user.id}` }}
            <span v-if="membership.user.username">(@{{ membership.user.username }})</span>
            <span v-if="membership.role !== 'member'"> - {{ membership.role }}</span>
          </option>
        </select>
        <button
          class="btn-primary btn-sm"
          :disabled="!newProposalProposedOwnerId || creatingProposal"
          @click="createProposal"
          style="min-height: 38px"
        >
          <span v-if="creatingProposal">{{ s__('ProjectSettings|Creating...') }}</span>
          <span v-else>{{ s__('ProjectSettings|Create Proposal') }}</span>
        </button>
      </div>
      <div v-if="proposalError" class="error" style="margin-top: 8px">{{ proposalError }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import TrashIcon from '../common/icons/TrashIcon.vue'

const props = defineProps({
  projectId: {
    type: String,
    required: true,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:expanded'])

const proposals = ref([])
const memberships = ref([])
const newProposalProposedOwnerId = ref(null)
const creatingProposal = ref(false)
const proposalError = ref('')
const revokingProposalId = ref(null)

const PROPOSALS_QUERY = gql`
  query ProjectOwnershipTransferProposals($id: ID!) {
    project(id: $id) {
      id
      ownershipTransferProposals {
        id
        status
        deliveryAttempts
        errors
        history
        currentOwner {
          id
          username
          name
        }
        proposedOwner {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`

const MEMBERSHIPS_QUERY = gql`
  query ProjectMemberships($id: ID!) {
    project(id: $id) {
      id
      memberships {
        id
        role
        user {
          id
          username
          name
        }
      }
    }
  }
`

const CREATE_PROPOSAL = gql`
  mutation CreateOwnershipTransferProposal($projectId: ID!, $proposedOwnerId: ID!) {
    createOwnershipTransferProposal(projectId: $projectId, proposedOwnerId: $proposedOwnerId) {
      proposal {
        id
        status
        deliveryAttempts
        errors
        history
        currentOwner {
          id
          username
          name
        }
        proposedOwner {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const REVOKE_PROPOSAL = gql`
  mutation RevokeOwnershipTransferProposal($proposalId: ID!) {
    revokeOwnershipTransferProposal(proposalId: $proposalId) {
      proposal {
        id
        status
        deliveryAttempts
        errors
        history
        currentOwner {
          id
          username
          name
        }
        proposedOwner {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const {
  load: loadProposals,
  result: proposalsResult,
  loading,
  refetch: refetchProposals,
} = useLazyQuery(PROPOSALS_QUERY, () => ({
  id: props.projectId,
}))

const {
  load: loadMemberships,
  result: membershipsResult,
  loading: loadingMemberships,
} = useLazyQuery(MEMBERSHIPS_QUERY, () => ({
  id: props.projectId,
}))

const { mutate: createProposalMutation } = useMutation(CREATE_PROPOSAL)
const { mutate: revokeProposalMutation } = useMutation(REVOKE_PROPOSAL)

// Computed property for available members (excluding owner)
const availableMembers = computed(() => {
  return memberships.value.filter(m => m.role !== 'owner')
})

// Watch for query results
watch(
  proposalsResult,
  data => {
    if (data?.project?.ownershipTransferProposals) {
      proposals.value = data.project.ownershipTransferProposals || []
    }
  },
  { immediate: true }
)

watch(
  membershipsResult,
  data => {
    if (data?.project?.memberships) {
      memberships.value = data.project.memberships || []
    }
  },
  { immediate: true }
)

// Watch for expanded prop to load data
watch(
  () => props.expanded,
  expanded => {
    if (expanded && !proposalsResult.value && !loading.value) {
      loadProposals()
    }
    if (expanded && !membershipsResult.value && !loadingMemberships.value) {
      loadMemberships()
    }
  },
  { immediate: true }
)

// Clear error when user selects another entry
watch(newProposalProposedOwnerId, () => {
  proposalError.value = ''
})

function formatProposalStatus(status) {
  const statusMap = {
    to_deliver: s__('ProjectSettings|Pending'),
    active: s__('ProjectSettings|Pending'),
    accepted: s__('ProjectSettings|Accepted'),
    rejected: s__('ProjectSettings|Rejected'),
    expired: s__('ProjectSettings|Expired'),
    revoked: s__('ProjectSettings|Revoked'),
    failed_to_deliver: s__('ProjectSettings|Failed to Deliver'),
  }
  return statusMap[status] || status
}

async function createProposal() {
  if (!newProposalProposedOwnerId.value) return

  creatingProposal.value = true
  proposalError.value = ''

  try {
    const { data } = await createProposalMutation({
      projectId: props.projectId,
      proposedOwnerId: String(newProposalProposedOwnerId.value),
    })

    if (data?.createOwnershipTransferProposal?.errors?.length) {
      proposalError.value = data.createOwnershipTransferProposal.errors.join(', ')
    } else {
      newProposalProposedOwnerId.value = null
      proposalError.value = ''

      // Ensure section is expanded
      if (!props.expanded) {
        emit('update:expanded', true)
      }

      // Refetch proposals to get the new one
      if (proposalsResult.value) {
        const result = await refetchProposals()
        if (result?.data?.project?.ownershipTransferProposals) {
          proposals.value = result.data.project.ownershipTransferProposals || []
        }
      } else {
        await loadProposals()
      }
    }
  } catch (e) {
    proposalError.value =
      s__('ProjectSettings|Failed to create ownership transfer proposal') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    creatingProposal.value = false
  }
}

async function revokeProposal(proposal) {
  const confirmed = await showConfirm(
    sprintf(
      s__(
        'ProjectSettings|Are you sure you want to revoke the ownership transfer proposal for %{user}?'
      ),
      {
        user:
          proposal.proposedOwner.name ||
          proposal.proposedOwner.username ||
          `User ${proposal.proposedOwner.id}`,
      }
    )
  )
  if (!confirmed) return

  revokingProposalId.value = proposal.id

  try {
    const { data } = await revokeProposalMutation({
      proposalId: proposal.id,
    })

    if (data?.revokeOwnershipTransferProposal?.errors?.length) {
      alert(data.revokeOwnershipTransferProposal.errors.join(', '))
    } else {
      // Refetch proposals to reflect the revoked status
      if (proposalsResult.value) {
        const result = await refetchProposals()
        if (result?.data?.project?.ownershipTransferProposals) {
          proposals.value = result.data.project.ownershipTransferProposals || []
        }
      } else {
        await loadProposals()
      }
    }
  } catch (e) {
    alert(
      s__('ProjectSettings|Failed to revoke ownership transfer proposal') +
        ': ' +
        (e.message || s__('ProjectSettings|Unknown error'))
    )
  } finally {
    revokingProposalId.value = null
  }
}
</script>

<style scoped>
.ownership-transfer {
  padding: 0;
}

.proposal-item {
  background: #fff;
}

.status-badge {
  display: inline-block;
}

.status-to_deliver {
  background: #fff3cd;
  color: #856404;
}

.status-active {
  background: #d1ecf1;
  color: #0c5460;
}

.status-accepted {
  background: #d4edda;
  color: #155724;
}

.status-rejected {
  background: #f8d7da;
  color: #721c24;
}

.status-revoked {
  background: #e2e3e5;
  color: #383d41;
}

.status-failed_to_deliver {
  background: #f8d7da;
  color: #721c24;
}

.muted {
  color: #6c757d;
  font-size: 14px;
}

.error {
  color: #dc3545;
  font-size: 14px;
  padding: 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
}

.form-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
}

/* Button styles inherit from forms.css */
</style>
